// filepath: src/pages/MyCamerasPage.jsx
import { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { createPortal } from 'react-dom';
import '../css/cameras.css';
import { STREAM_API, apiFetch, getToken, resetSession } from '../utils/streamApi';
import { getAlertPollMs, getIdleReloadMs } from '../utils/alertPollConfig';
import { requestFullscreen, exitFullscreen, onFullscreenExit } from '../utils/fullscreen';
import { useSize } from '../contexts/SizeContext';
import useIsMobile from '../utils/useIsMobile';
import CamOfflineModal from '../components/CamOfflineModal';
import PlaybackSpeedButton from '../components/PlaybackSpeedButton';
import { productHas } from '../utils/entitlements';

// Delay before re-attempting the live HLS stream after a fatal error, for
// cameras that have no MJPEG fallback (record-only — no RTSPReader).
const LIVE_RETRY_MS = 3000;

// While parked on the MJPEG fallback (~2-3fps), periodically retry HLS so a
// tile returns to smooth video once the recorder/playlist recovers, instead
// of sitting on MJPEG until the user manually hits Reconnect (PRVSK-54).
const MJPEG_RETRY_MS = 30000;

// PRVSK-56: /hls/{idx}/live.m3u8 can swap which underlying stream it serves
// under the same URL (sub-stream recorder output vs. an independent HD
// pipeline) — two unrelated playlists with their own segment numbering and
// no EXT-X-DISCONTINUITY between them. hls.js has no way to know a swap
// happened; it can wait on the old sequence forever while the tile sits
// frozen under a LIVE badge. If currentTime hasn't advanced for this long
// while we believe we're playing, force a full reconnect rather than trust
// hls.js to notice on its own.
const LIVE_STALL_MS = 6000;

// PRVSK-57: on b140s51 the cloud→viewer leg fell behind real time under
// 9-tile wall load while the device/cameras stayed clean — hls.js has no way
// to shed load, so every tile just kept trying to catch up and froze. When a
// wall tile's segment fetches are consistently slower than the segment's own
// duration, drop that tile to a low-rate single-frame snapshot poll instead
// of leaving hls.js to grind — one small request every couple seconds instead
// of a continuous stream the pipe can't keep up with — then retry HLS later.
const WALL_DEGRADE_SLOW_RATIO    = 1.5;   // fetch time vs EXTINF duration to count as "slow"
const WALL_DEGRADE_TRIGGER_COUNT = 3;     // consecutive slow fragments before degrading
const WALL_DEGRADE_SNAPSHOT_MS   = 2000;  // snapshot poll interval while degraded
const WALL_DEGRADE_RETRY_MS      = 20000; // how long to stay degraded before retrying HLS

// PRVSK-93: per-player id sent as `cid` on live.m3u8 and as `session` on
// /live-hq/start|stop. The device serves HD only to the players holding it, so
// one viewer's fullscreen no longer switches every other viewer's wall tile of
// that camera to HD, and one viewer closing it no longer drops anyone else's.
// getRandomValues, not randomUUID — plain-HTTP LAN boxes have no secure context.
const newPlayerId = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(12)), b => b.toString(16).padStart(2, '0')).join('');

// ═════════════════════════════════════════════════════════════════════════════
// Map raw API camera → UI camera shape
// streamIdx = position in CAMERA_SOURCES array (used for /cameras/{idx}/stream)
// ═════════════════════════════════════════════════════════════════════════════
export function mapCamera(cam, idx, device) {
  return {
    id:        cam.name.toLowerCase().replace(/\s+/g, '-'),
    streamIdx: idx,
    name:      cam.name.replace(/\b\w/g, c => c.toUpperCase()),
    channel:   cam.channel,
    ip:        cam.ip || device?.ip || '',
    type:      cam.type || '',
    location:  cam.zone || cam.name,
    enabled:   cam.enabled !== false,
    // 'full' | 'checkin' | 'off'. Record-only cameras have no RTSPReader,
    // so they have no MJPEG fallback — the player must retry HLS instead.
    detectMode: cam.detect_mode || 'full',
    status:    'online',
    fps:       15,
    onvif_port: cam.onvif_port || null,
  };
}

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// canvas.toBlob() is async, and by the time its callback runs, iOS Safari has
// already expired the click's "user activation" — so navigator.share() called
// from inside that callback silently loses the share sheet (falls through to
// a plain download with no folder choice). Decode the dataURL synchronously
// instead, so the whole save stays inside the original click's activation
// window and share() actually gets to offer "Save to Files".
function dataUrlToBlob(dataUrl) {
  const [header, base64] = dataUrl.split(',');
  const mime   = header.match(/:(.*?);/)[1];
  const binary = atob(base64);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

// PRVSK-66: the Android WebView app can't hand a blob: URL to its native
// download path (net::ERR_FILE_NOT_FOUND) — MainActivity's FlutterDownload
// channel (see main.dart) exists specifically for this, and every OTHER save
// path in this app (AlertsPage, DetectionsPage, DiagnosticsPage,
// alertLogExport) already checks for it first. This was the one place that
// didn't, so Crop & Save on Android always hit that native error.
async function downloadBlob(blob, filename) {
  if (window.FlutterDownload) {
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    window.FlutterDownload.postMessage(JSON.stringify({ filename, mime: blob.type || 'application/octet-stream', data: base64 }));
    return;
  }
  // Route through the share sheet on iOS ("Save to Files" lets the user pick a
  // folder); everywhere else a plain download is the expected, predictable
  // behavior (same pattern as AlertsPage's fetchAndSaveFile).
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ═════════════════════════════════════════════════════════════════════════════
// Cropper Overlay
// ═════════════════════════════════════════════════════════════════════════════
function CropperOverlay({ dataUrl, cameraName, onClose }) {
  const canvasRef = useRef(null);
  const imgRef    = useRef(new Image());
  const [selecting, setSelecting] = useState(false);
  const [start, setStart]         = useState(null);
  const [end,   setEnd]           = useState(null);
  const [saving,  setSaving]      = useState(false);
  const [saveMsg, setSaveMsg]     = useState(null);

  useEffect(() => {
    const img  = imgRef.current;
    img.onload = () => {
      const c = canvasRef.current;
      c.width  = img.width;
      c.height = img.height;
      c.getContext('2d').drawImage(img, 0, 0);
    };
    img.src = dataUrl;
  }, [dataUrl]);

  useEffect(() => {
    if (!start || !end) return;
    const c   = canvasRef.current;
    const ctx = c.getContext('2d');
    const img = imgRef.current;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0);
    const x = Math.min(start.x, end.x), y = Math.min(start.y, end.y);
    const w = Math.abs(end.x - start.x), h = Math.abs(end.y - start.y);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, x, y, w, h, x, y, w, h);
    ctx.strokeStyle = '#27ae60'; ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w, h);
  }, [start, end]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * canvasRef.current.width / r.width, y: (e.clientY - r.top) * canvasRef.current.height / r.height };
  };

  // A drag under 5px either way isn't a deliberate crop (a tap/click that
  // barely moved) — treat it the same as no selection at all.
  const hasSelection = !!(start && end
    && Math.abs(end.x - start.x) >= 5 && Math.abs(end.y - start.y) >= 5);

  // Async, but every step up to and including navigator.share() runs
  // synchronously (no awaited gap before it) — that's what keeps this inside
  // the click's user-activation window on iOS Safari. See dataUrlToBlob.
  const cropAndSave = async () => {
    if (saving) return;
    // No (or too-small) selection → save the full frame instead of silently
    // doing nothing, which is what this did before: Save required a crop
    // first with no way to just keep the whole image.
    let x, y, w, h;
    if (hasSelection) {
      x = Math.min(start.x, end.x); y = Math.min(start.y, end.y);
      w = Math.abs(end.x - start.x); h = Math.abs(end.y - start.y);
    } else {
      x = 0; y = 0; w = imgRef.current.width; h = imgRef.current.height;
    }
    const out = document.createElement('canvas');
    out.width = w; out.height = h;
    out.getContext('2d').drawImage(imgRef.current, x, y, w, h, 0, 0, w, h);
    const filename = `${cameraName}_${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`;
    const blob = dataUrlToBlob(out.toDataURL('image/jpeg', 0.95));

    setSaving(true);
    try {
      if (isIOS() && navigator.share) {
        const file = new File([blob], filename, { type: 'image/jpeg' });
        if (!navigator.canShare || navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file] });
            setSaveMsg('✅ Shared');
          } catch (err) {
            setSaving(false);
            if (err?.name === 'AbortError') { onClose(); return; }   // user dismissed the sheet
            setSaveMsg('⚠️ Save failed');
            setTimeout(onClose, 1200);
            return;
          }
        } else {
          await downloadBlob(blob, filename);
          setSaveMsg('✅ Saved to Downloads');
        }
      } else {
        await downloadBlob(blob, filename);
        setSaveMsg('✅ Saved to Downloads');
      }
    } catch {
      setSaveMsg('⚠️ Save failed');
    }
    setSaving(false);
    setTimeout(onClose, 1200);
  };

  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.92)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}
      onClick={e => e.stopPropagation()}
    >
      <p style={{ color:'#ccc', fontSize:13, margin:0 }}>Drag to select an area, or just Save for the full image</p>
      <canvas ref={canvasRef} style={{ maxWidth:'90vw', maxHeight:'72vh', cursor:'crosshair', borderRadius:6, touchAction:'none' }}
        onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); setSelecting(true); const p = pos(e); setStart(p); setEnd(p); }}
        onPointerMove={(e) => { if (selecting) setEnd(pos(e)); }}
        onPointerUp={() => setSelecting(false)}
        onPointerCancel={() => setSelecting(false)}
      />
      <div style={{ display:'flex', gap:12 }}>
        <button onClick={cropAndSave} disabled={saving}
          style={{ padding:'8px 22px', background:'#27ae60', color:'white', border:'none', borderRadius:6, cursor: saving ? 'default' : 'pointer', fontSize:14, opacity: saving ? 0.7 : 1 }}>
          {saving ? '⏳ Saving…' : hasSelection ? '✂️ Crop & Save' : '💾 Save Full Image'}
        </button>
        <button onClick={onClose} disabled={saving}
          style={{ padding:'8px 22px', background:'#444', color:'white', border:'none', borderRadius:6, cursor: saving ? 'default' : 'pointer', fontSize:14, opacity: saving ? 0.7 : 1 }}>
          Cancel
        </button>
      </div>
      {saveMsg && (
        <div style={{ position:'fixed', bottom:'8%', left:'50%', transform:'translateX(-50%)',
          background:'rgba(20,20,20,0.95)', color:'#fff', padding:'8px 18px', borderRadius:20,
          fontSize:13, fontWeight:600, zIndex:10000 }}>
          {saveMsg}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Video Player — HLS live stream via HLS.js
// ═════════════════════════════════════════════════════════════════════════════
// hdPriority: "high" (default) for a deliberate inspect-this-camera view (wall
// focused tile, true fullscreen) vs "low" for a casual glance-through (mobile
// dashboard swiper). Only changes how the backend arbitrates the shared
// on-demand HD slot cap under contention — see /hls/{idx}/live-hq/start.
export function VideoPlayer({ camera, streamToken, isLarge = false, onPhaseChange, hdPriority = 'high', wallDegrade = false }) {
  const videoRef   = useRef(null);
  const hlsRef     = useRef(null);
  const retryTimer = useRef(null);
  const mjpegRef   = useRef(null);
  const playerId   = useRef(null);
  if (!playerId.current) playerId.current = newPlayerId();
  const isMobile   = useIsMobile();
  const [phase,       setPhase]       = useState('connecting');
  const [dots,        setDots]        = useState('');
  const [cropperData, setCropperData] = useState(null);
  const [retryKey,    setRetryKey]    = useState(0);
  // PRVSK-93: HD was asked for but the device's HD slot cap is full (429).
  const [hdLimited,   setHdLimited]   = useState(false);
  // PRVSK-57: wall-tile bandwidth degrade — see WALL_DEGRADE_* above.
  const [degraded,      setDegraded]      = useState(false);
  const [snapshotTick,  setSnapshotTick]  = useState(0);
  const slowStreakRef   = useRef(0);
  const wasDegradedRef  = useRef(false);
  const degradeTimer    = useRef(null);
  // MJPEG fallback: when the HLS recorder isn't producing a playlist (recording
  // off / not started), stream the detection-fed MJPEG instead — always available
  // when the camera is up, no extra RTSP connection. Decouples live from recording.
  const [useMjpeg,    setUseMjpeg]    = useState(false);

  const updatePhase = (p) => { setPhase(p); onPhaseChange?.(p); };
  const reconnect   = () => { clearTimeout(retryTimer.current); setRetryKey(k => k + 1); };

  // Reconnect when user returns to this tab
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setRetryKey(k => k + 1);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // HD while expanded. Grid tiles play the sub-stream, which the recorder emits
  // as a byproduct of segmenting to disk and so costs nothing extra — but ten
  // main-stream tiles would, and the cost lands on the browser, which has to
  // decode them all. One expanded camera is comfortable on any laptop or phone,
  // so ask the backend for a main-stream pipeline only while this player is the
  // large one. The hold belongs to this player (playerId): only this player's
  // own playlist fetches get the HD file and keep the hold alive, and the
  // backend drops it ~30s after the last one, so a closed tab or a shut laptop
  // releases it even if the cleanup below never runs (PRVSK-93).
  //
  // The sub-stream deliberately keeps playing while the pipeline warms up (it
  // needs a few seconds to connect and cut a first segment). No client-side
  // swap is needed once it's ready: this is still the same live.m3u8 URL, and
  // the existing hls.js instance already re-fetches a live playlist on its own
  // every target duration — the backend (hls_live_playlist / gated on
  // _live_playlist_fresh) only starts actually serving the HD file once it has
  // a real buffer of segments, so that ordinary refresh picks it up cleanly.
  // This used to call reconnect() here, which destroyed and recreated the
  // whole hls.js instance for the swap — a visible black "Connecting" flash
  // plus a backward time-jump, because the fresh instance re-positioned itself
  // against liveSyncDurationCount on a playlist that had barely any segments
  // yet. The poll below now only exists to know when to stop polling.
  useEffect(() => {
    if (!isLarge || !streamToken) return;
    const idx = camera.streamIdx;
    let cancelled = false;
    let started   = false;
    let poll      = null;
    const session = playerId.current;
    setHdLimited(false);

    // Wait before asking. The mobile dashboard is a swipeable carousel and the
    // grid opens cameras on a click, so a viewer can pass through several in a
    // few seconds — spawning and killing an ffmpeg for each would churn the
    // camera's RTSP sessions for pictures nobody looked at. Only request HD once
    // someone has actually settled on a camera.
    const settle = setTimeout(() => {
      started = true;
      apiFetch(`/hls/${idx}/live-hq/start`, { method: 'POST', json: { priority: hdPriority, session } })
        .then((res) => {
          // fetch() only rejects on a network failure, not an HTTP error
          // status — a 429 (HD slot cap hit) or 400 used to fall through to
          // here and poll /live-hq once a second for as long as the player
          // stayed open, for a pipeline that was never started.
          if (res.status === 429 && !cancelled) setHdLimited(true);
          if (!res.ok || cancelled) return;
          poll = setInterval(async () => {
            if (cancelled) return;
            try {
              const res2 = await apiFetch(`/hls/${idx}/live-hq`);
              const s    = res2.ok ? await res2.json() : null;
              if (s?.ready && !cancelled) {
                clearInterval(poll); poll = null;
              }
            } catch { /* transient — keep polling */ }
          }, 1000);
        })
        .catch(() => { /* HD limit reached, or offline — sub-stream is fine */ });
    }, 2500);

    return () => {
      cancelled = true;
      clearTimeout(settle);
      if (poll) clearInterval(poll);
      // Only if we actually asked for one — swiping past a camera should not
      // generate a stop for a pipeline that was never started.
      // Releases only this player's hold; the device keeps the pipeline for
      // any other viewer still holding it.
      if (started) apiFetch(`/hls/${idx}/live-hq/stop`, { method: 'POST', json: { session } }).catch(() => {});
    };
  }, [isLarge, camera.streamIdx, streamToken, hdPriority]);

  useEffect(() => {
    clearTimeout(retryTimer.current);
    setUseMjpeg(false);            // each (re)try starts by attempting HLS
    slowStreakRef.current = 0;
    if (!streamToken || !videoRef.current) {
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      updatePhase('connecting');
      return;
    }
    updatePhase('connecting');
    const url = `${STREAM_API}/hls/${camera.streamIdx}/live.m3u8?token=${streamToken}&cid=${playerId.current}`;
    const videoEl = videoRef.current;
    // The manifest/metadata events below only mean HLS.js (or Safari) now knows
    // enough to attempt playback — the first real video frame is still a fetch
    // + decode away. Flipping to "LIVE" there tags a still-black tile as live
    // (looks like the camera is off). The native `playing` event is what
    // actually fires once frames are being produced, so gate the badge on that.
    const onPlaying = () => updatePhase('playing');
    videoEl.addEventListener('playing', onPlaying);

    if (Hls.isSupported()) {
      const tag = `[HLS cam${camera.streamIdx}]`;
      // enableWorker: true moves TS demuxing off the main thread — with 9-12
      // wall tiles decoding at once, main-thread demuxing directly competes
      // with React's own rendering and is the single biggest contributor to
      // a laggy wall view.
      // PRVSK-54: ~1 segment of buffer (liveSyncDurationCount:1, maxBufferLength:6
      // against 1.5-3s segments) meant any late segment stalled then seeked to the
      // live edge — visible as skipped frames on tiles whose source was otherwise
      // clean. Wider buffer/latency targets + maxLiveSyncPlaybackRate let hls.js
      // catch up by speeding up playback instead of seeking, at the cost of
      // ~6-8s latency instead of ~3s.
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false, backBufferLength: 2, maxBufferLength: 12, liveSyncDurationCount: 3, liveMaxLatencyDurationCount: 10, maxLiveSyncPlaybackRate: 1.2 });
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(videoRef.current);
      // Verbose HLS logging is only worth its cost when there's a desktop
      // devtools console to read it from. isLarge alone used to gate this, but
      // isLarge is also what the mobile dashboard swiper sets for whichever
      // camera is currently on screen — so on a phone this ran continuously
      // for no one to see, for as long as the dashboard was open.
      const debugVerbose = isLarge && !isMobile;
      // Stream URLs carry the login token (?token=) — never print it (PRVSK-77).
      const noToken = u => String(u ?? '').replace(/([?&]token=)[^&]*/g, '$1…');
      hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
        if (debugVerbose) console.log(tag, 'Manifest parsed — levels:', JSON.stringify(data.levels?.map(l => ({
          codec: l.videoCodec, width: l.width, height: l.height, bitrate: l.bitrate, url: noToken(l.url)
        }))));
        // Stays "connecting" (spinner + black tile) until the `playing` listener
        // above fires — this only means playback is about to be attempted.
        videoRef.current?.play().catch(e => console.error(tag, 'play() rejected:', e.name, e.message));
      });
      // The per-level/per-fragment logging below is only cheap for a single
      // enlarged desktop stream — on a 9-12 tile wall it's 30-50+ console
      // writes and object allocations every ~2s, all fighting the same main
      // thread the tiles are trying to render on.
      if (debugVerbose) {
        hls.on(Hls.Events.LEVEL_LOADED, (_, data) => {
          const frags = data.details?.fragments?.slice(0, 2).map(f => noToken(f.url)) ?? [];
          console.log(tag, 'Level loaded — first 2 segment URLs:', frags);
        });
        hls.on(Hls.Events.BUFFER_CODECS, (_, data) => {
          console.log(tag, 'Buffer codecs:', data);
        });
        hls.on(Hls.Events.FRAG_LOADED, (_, data) => {
          const buf = data.payload;
          const bytes = buf ? new Uint8Array(buf, 0, Math.min(8, buf.byteLength)) : null;
          const hex   = bytes ? Array.from(bytes).map(b => '0x' + b.toString(16).padStart(2,'0')).join(' ') : 'no payload';
          const valid = bytes && bytes[0] === 0x47;
          console.log(tag, `Fragment sn:${data.frag.sn} dur:${data.frag.duration?.toFixed(2)}s size:${buf?.byteLength}B first-bytes:[${hex}] MPEG-TS:${valid}`);
        });
      }
      // PRVSK-57: only wall tiles opt in — the focused/fullscreen view should
      // keep fighting for HLS via the existing stall watchdog, not bail to a
      // snapshot the moment one segment lags.
      if (wallDegrade) {
        hls.on(Hls.Events.FRAG_LOADED, (_, data) => {
          const stats      = data.frag?.stats;
          const durationMs = (data.frag?.duration || 0) * 1000;
          if (!stats?.loading || !durationMs) return;
          const fetchMs = stats.loading.end - stats.loading.start;
          if (fetchMs > durationMs * WALL_DEGRADE_SLOW_RATIO) {
            slowStreakRef.current += 1;
            if (slowStreakRef.current >= WALL_DEGRADE_TRIGGER_COUNT) {
              slowStreakRef.current = 0;
              setDegraded(true);
            }
          } else {
            slowStreakRef.current = 0;
          }
        });
      }
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.response?.code === 401) {
          hls.destroy(); hlsRef.current = null;
          resetSession();
          window.location.href = '/login';
          return;
        }
        if (data.fatal) {
          console.warn(tag, 'Fatal error — type:', data.type, 'details:', data.details, data.error?.message || '');
          hls.destroy(); hlsRef.current = null;
          // MJPEG is served from the RTSPReader the detection worker keeps open,
          // so a record-only camera has no reader and no MJPEG — falling back
          // there strands it on "Connecting" until a manual reload. Its live
          // view comes from the NVR playlist, which is still being written, so
          // retry HLS instead.
          if (camera.detectMode === 'off') {
            updatePhase('connecting');
            retryTimer.current = setTimeout(() => setRetryKey(k => k + 1), LIVE_RETRY_MS);
            return;
          }
          // Otherwise fall back to the always-available MJPEG stream instead of
          // going offline. The MJPEG <img> reports its own load/error; the user
          // can Reconnect to retry HLS.
          setUseMjpeg(true);
          updatePhase('connecting');
        }
        // non-fatal (bufferSeekOverHole, bufferStalledError) are self-healed by HLS.js
      });
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = url;
      videoRef.current.addEventListener('loadedmetadata', () => {
        // Same reasoning as the HLS.js branch — wait for the `playing` listener.
        videoRef.current?.play().catch(() => {});
      }, { once: true });
      videoRef.current.addEventListener('error', () => {
        setUseMjpeg(true);          // no HLS → fall back to MJPEG stream
        updatePhase('connecting');
      }, { once: true });
    }

    return () => {
      clearTimeout(retryTimer.current);
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      videoEl.removeEventListener('playing', onPlaying);
      videoEl.src = '';
    };
  }, [camera.streamIdx, streamToken, retryKey]); // eslint-disable-line

  // Drop the MJPEG connection when this player switches camera/stream or
  // unmounts, so the backend stream generator exits instead of running on
  // forever for a removed camera ([STREAM][CAM N] reader not ready spam).
  useEffect(() => {
    const node = mjpegRef.current;
    return () => { if (node) { try { node.src = ''; } catch {} } };
  }, [useMjpeg, camera.streamIdx]);

  // While stuck on the MJPEG fallback, retry HLS periodically instead of only
  // on a manual Reconnect — see MJPEG_RETRY_MS above.
  useEffect(() => {
    if (!useMjpeg) return;
    const idx = camera.streamIdx;
    let cancelled = false;
    const iv = setInterval(async () => {
      if (cancelled) return;
      try {
        // Probe before remounting hls.js. Every retry used to force
        // setUseMjpeg(false) + phase 'connecting' unconditionally, so a
        // camera with no HLS stream at all (e.g. NVR disabled) went black
        // every 30s forever regardless, only to land right back on MJPEG
        // once hls.js's own fatal error fired again. Only remount if the
        // playlist is actually there.
        const res = await apiFetch(`/hls/${idx}/live.m3u8`);
        if (!cancelled && res.ok) setRetryKey(k => k + 1);
      } catch { /* offline — try again next interval */ }
    }, MJPEG_RETRY_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [useMjpeg, camera.streamIdx]);

  // Animate dots while connecting
  useEffect(() => {
    if (phase !== 'connecting') return;
    const iv = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 400);
    return () => clearInterval(iv);
  }, [phase]);

  // Playback watchdog (PRVSK-56) — see LIVE_STALL_MS above. Only meaningful
  // once real frames are flowing (phase 'playing') and only for the <video>
  // element itself, not the MJPEG fallback <img>.
  useEffect(() => {
    if (phase !== 'playing' || useMjpeg || degraded) return;
    let lastTime   = videoRef.current?.currentTime ?? 0;
    let lastChange = Date.now();
    const iv = setInterval(() => {
      const el = videoRef.current;
      if (!el) return;
      if (el.currentTime !== lastTime) {
        lastTime = el.currentTime;
        lastChange = Date.now();
        return;
      }
      if (!el.paused && Date.now() - lastChange > LIVE_STALL_MS) {
        console.warn(`[HLS cam${camera.streamIdx}] playback stalled ${LIVE_STALL_MS}ms — reconnecting`);
        updatePhase('connecting');
        reconnect();
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [phase, useMjpeg, degraded, camera.streamIdx]); // eslint-disable-line

  // PRVSK-57: while degraded, stop hls.js entirely (destroy releases the
  // segment/playlist requests it was falling behind on) and poll a single
  // frame instead. Coming back out only bumps retryKey — the transition
  // guard (wasDegradedRef) keeps this from firing on mount, when degraded
  // starts false and there's nothing to resume.
  useEffect(() => {
    if (!wallDegrade) return;
    if (degraded) {
      wasDegradedRef.current = true;
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      degradeTimer.current = setTimeout(() => setDegraded(false), WALL_DEGRADE_RETRY_MS);
      return () => clearTimeout(degradeTimer.current);
    }
    if (wasDegradedRef.current) {
      wasDegradedRef.current = false;
      setRetryKey(k => k + 1);
    }
  }, [degraded, wallDegrade]);

  useEffect(() => {
    if (!degraded) return;
    const iv = setInterval(() => setSnapshotTick(t => t + 1), WALL_DEGRADE_SNAPSHOT_MS);
    return () => clearInterval(iv);
  }, [degraded]);

  const statusColor = () => {
    if (degraded) return '#f39c12';
    if (phase === 'playing') return '#27ae60';
    if (phase === 'offline') return '#e74c3c';
    return '#f39c12';
  };

  // Use the frame endpoint for snapshots — clean single-JPEG fetch, no MJPEG parsing
  const [capturing, setCapturing] = useState(false);
  const captureSnapshot = async () => {
    if (!streamToken || capturing) return;
    setCapturing(true);
    try {
      const res = await fetch(`${STREAM_API}/cameras/${camera.streamIdx}/frame?token=${streamToken}`);
      if (!res.ok) { setCapturing(false); return; }
      const blobUrl = URL.createObjectURL(await res.blob());
      const img = new Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        setCropperData(c.toDataURL('image/jpeg', 0.95));
        URL.revokeObjectURL(blobUrl);
        setCapturing(false);
      };
      img.onerror = () => { URL.revokeObjectURL(blobUrl); setCapturing(false); };
      img.src = blobUrl;
    } catch (e) {
      if (e.name !== 'AbortError') console.error('Snapshot failed:', e);
      setCapturing(false);
    }
  };

  const [isMuted, setIsMuted] = useState(true);
  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !isMuted;
    videoRef.current.muted = next;
    setIsMuted(next);
  };

  return (
    <div className={`video-player-wrapper ${isLarge ? 'large' : ''}`}>
      <video ref={videoRef} autoPlay muted playsInline className="video-canvas"
        style={{ objectFit: 'contain', display: (useMjpeg || degraded) ? 'none' : 'block' }} />
      {useMjpeg && !degraded && streamToken && (
        <img ref={mjpegRef} className="video-canvas" style={{ objectFit: 'contain' }} alt=""
          src={`${STREAM_API}/cameras/${camera.streamIdx}/stream?token=${streamToken}`}
          onLoad={() => updatePhase('playing')}
          onError={() => updatePhase('offline')} />
      )}
      {degraded && streamToken && (
        // PRVSK-57: one JPEG every WALL_DEGRADE_SNAPSHOT_MS instead of a
        // stream the cloud→viewer leg can't keep up with.
        <img className="video-canvas" style={{ objectFit: 'contain' }} alt=""
          src={`${STREAM_API}/cameras/${camera.streamIdx}/frame?token=${streamToken}&_=${snapshotTick}`}
          onLoad={() => updatePhase('playing')} />
      )}
      {phase !== 'playing' && (
        // A recent snapshot behind the spinner instead of flat black. This is
        // what shows during a fullscreen cold-start (a brand-new VideoPlayer
        // mounts in the portal while the grid tile was already playing this
        // camera a moment before) or an HD-quality swap — there's almost
        // always a real picture worth showing instead of a jarring blackout.
        // Silently falls back to the plain background if the fetch 404s
        // (no streamToken yet, or the camera is genuinely offline).
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          backgroundColor: '#010e01',
          backgroundImage: streamToken
            ? `url(${STREAM_API}/cameras/${camera.streamIdx}/frame?token=${streamToken})`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }} />
      )}

      <div className="video-overlay" style={{ zIndex:2 }}>
        <div className="video-top-bar">
          <div className="live-indicator">
            {phase === 'playing' && <span className="rec-dot" />}
            <span className="live-dot" style={{ background: statusColor() }} />
            <span className="live-text">
              {degraded ? 'LOW BANDWIDTH'
                : phase === 'playing' ? 'LIVE'
                : phase === 'offline' ? 'OFFLINE' : 'CONNECTING'}
            </span>
            {/* PRVSK-93: say why a large view is still soft instead of silently staying SD. */}
            {isLarge && hdLimited && phase === 'playing' && !degraded && (
              <span className="live-text" title="All HD slots on this device are in use — showing the standard stream"
                style={{ marginLeft: 6, opacity: 0.8 }}>
                · SD (HD limit reached)
              </span>
            )}
          </div>
        </div>

        {phase === 'connecting' && (
          <div className="connecting-overlay">
            <div className="connecting-spinner" />
            <span className="connecting-label">Connecting{dots}</span>
          </div>
        )}
        {phase === 'offline' && (
          <div className="video-status-overlay">
            <span className="status-icon">📡</span>
            <span className="status-text">Camera Offline</span>
            <button className="reconnect-btn" onClick={reconnect}>&#x21BA; Reconnect</button>
          </div>
        )}
        {phase === 'playing' && (
          <div className="video-info-overlay">
            {!useMjpeg && (
              <button className="snapshot-btn" onClick={e => { e.stopPropagation(); toggleMute(); }} title={isMuted ? 'Unmute' : 'Mute'}>
                {isMuted ? '🔇' : '🔊'}
              </button>
            )}
            <button className="snapshot-btn" onClick={e => { e.stopPropagation(); captureSnapshot(); }} disabled={capturing} title="Capture snapshot">
              {capturing ? '⏳' : '📷'}
            </button>
          </div>
        )}
      </div>

      {cropperData && createPortal(
        <CropperOverlay dataUrl={cropperData} cameraName={camera.name} onClose={() => setCropperData(null)} />,
        document.body
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Snapshot thumbnail used in Command sidebar
// ═════════════════════════════════════════════════════════════════════════════
function SnapshotThumb({ camera, streamToken, onPhaseChange }) {
  const [state, setState] = useState('loading'); // 'loading' | 'loaded' | 'error'
  const src = streamToken
    ? `${STREAM_API}/cameras/${camera.streamIdx}/frame?token=${streamToken}`
    : null;

  useEffect(() => { setState('loading'); }, [src]);

  return (
    <div style={{ position:'relative', width:'100%', height:'100%' }}>
      {/* Placeholder shown while loading or on error */}
      {state !== 'loaded' && (
        <div className="snapshot-thumb-placeholder">
          <span style={{ fontSize: 20, opacity: 0.4 }}>{state === 'error' ? '📡' : '📷'}</span>
        </div>
      )}
      {src && (
        <img
          src={src}
          className="snapshot-thumb"
          alt={camera.name}
          style={{ opacity: state === 'loaded' ? 1 : 0, transition: 'opacity 0.2s' }}
          onLoad={() => { setState('loaded'); onPhaseChange?.('playing'); }}
          onError={() => { setState('error'); onPhaseChange?.('error'); }}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Tab Bar
// ═════════════════════════════════════════════════════════════════════════════
function CameraTabBar({ active, onTab }) {
  const tabs = [
    { id:'grid',    label:'Grid View', icon:'⊞' },
    { id:'wall',    label:'Wall',      icon:'▦'  },
    { id:'command', label:'Command',   icon:'🎛'  },
  ];
  return (
    <div className="camera-tab-bar">
      {tabs.map(t => (
        <button key={t.id} className={`camera-tab-btn${active === t.id ? ' active' : ''}`} onClick={() => onTab(t.id)}>
          <span className="tab-icon">{t.icon}</span>{t.label}
        </button>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Focus Controls  (varifocal cameras — ONVIF ImagingService)
// ═════════════════════════════════════════════════════════════════════════════
// Step sizes sent to the backend as the `speed` field (doubles as distance/step)
const LENS_STEPS = [
  { label: '···', value: 0.05 },
  { label: '··',  value: 0.15 },
  { label: '·',   value: 0.30 },
];

function LensRow({ label, onStep, busy }) {
  const btnBase = {
    padding: '6px 14px', border: '1px solid var(--border)',
    borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer',
    fontSize: 13, background: 'var(--surface)', color: 'var(--text)',
    opacity: busy ? 0.5 : 1,
  };
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 38, flexShrink: 0 }}>{label}</span>
      {/* Near / Wide steps — largest first */}
      {[...LENS_STEPS].reverse().map(s => (
        <button key={`neg-${s.value}`} style={btnBase} disabled={busy}
          onClick={() => onStep(-s.value)} title={`Step ${s.value}`}>
          ◀{s.label}
        </button>
      ))}
      {/* Far / Tele steps */}
      {LENS_STEPS.map(s => (
        <button key={`pos-${s.value}`} style={btnBase} disabled={busy}
          onClick={() => onStep(s.value)} title={`Step ${s.value}`}>
          {s.label}▶
        </button>
      ))}
    </div>
  );
}

function FocusControls({ camera }) {
  const [errMsg,    setErrMsg]    = useState('');
  const [busy,      setBusy]      = useState(false);
  const [sharpness, setSharpness] = useState(null);
  const prevSharp   = useRef(null);
  const maxSharp    = useRef(0);

  // measure sharpness on mount — don't set max yet, let first step establish baseline
  useEffect(() => {
    maxSharp.current = 0;
    prevSharp.current = null;
    apiFetch(`/cameras/${camera.streamIdx}/focus/sharpness`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.sharpness != null) setSharpness(d.sharpness); })
      .catch(() => {});
  }, [camera.streamIdx]);

  const send = async (path, body) => {
    if (busy) return;
    setBusy(true);
    setErrMsg('');
    prevSharp.current = sharpness;
    try {
      const res = await apiFetch(path, { method: 'POST', json: body });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setErrMsg(d.detail || 'Request failed');
      } else {
        const d = await res.json().catch(() => ({}));
        if (d.sharpness != null) {
          if (d.sharpness > maxSharp.current) maxSharp.current = d.sharpness;
          setSharpness(d.sharpness);
        }
      }
    } catch {
      setErrMsg('Network error');
    } finally {
      setBusy(false);
    }
  };

  const focusStep = (step) => send(
    `/cameras/${camera.streamIdx}/focus/move`,
    { direction: step > 0 ? 'far' : 'near', speed: Math.abs(step) }
  );

  const zoomStep = (step) => send(
    `/cameras/${camera.streamIdx}/zoom/move`,
    { direction: step > 0 ? 'tele' : 'wide', speed: Math.abs(step) }
  );

  // derive hint from sharpness trend
  const hint = () => {
    if (sharpness == null) return null;
    const pct = maxSharp.current > 0 ? Math.min(sharpness / maxSharp.current, 1) : 0;
    // initial load — no step taken yet, always prompt to start
    if (prevSharp.current == null) {
      return { text: 'Click ·▶ Far once to start — bar will guide you', color: 'var(--text-muted)' };
    }
    const delta = sharpness - prevSharp.current;
    if (pct > 0.92)  return { text: 'Sharp — looks good!', color: '#22c55e' };
    if (delta > 1)   return { text: '↑ Getting sharper — keep going same direction', color: '#22c55e' };
    if (delta < -1)  return { text: '↓ Getting blurrier — try the other direction', color: '#f59e0b' };
    return            { text: 'No change — try a bigger step', color: 'var(--text-muted)' };
  };

  const h   = hint();
  const pct = maxSharp.current > 0 && sharpness != null
    ? Math.round(Math.min(sharpness / maxSharp.current, 1) * 100)
    : null;

  return (
    <div className="detail-section">
      <h4>Lens Control</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <LensRow label="Zoom"  onStep={zoomStep}  busy={busy} />
        <LensRow label="Focus" onStep={focusStep} busy={busy} />
      </div>

      {/* Sharpness meter */}
      {pct != null && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
            <span style={{ color: 'var(--text-muted)' }}>Sharpness</span>
            <span style={{ color: 'var(--text-muted)' }}>{pct}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: 3, transition: 'width 0.3s, background 0.3s',
              width: `${pct}%`,
              background: pct > 85 ? '#22c55e' : pct > 55 ? '#f59e0b' : '#ef4444',
            }} />
          </div>
        </div>
      )}

      <div style={{ marginTop: 6, minHeight: 18 }}>
        {errMsg && <span style={{ color: 'var(--danger)', fontSize: 12 }}>{errMsg}</span>}
        {!errMsg && h && <span style={{ fontSize: 12, color: h.color }}>{busy ? 'moving…' : h.text}</span>}
        {!errMsg && !h && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>◀··· large step &nbsp; ·▶ small step</span>}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Recordings Modal
// ═════════════════════════════════════════════════════════════════════════════
function RecordingsModal({ camera, streamToken, onClose }) {
  const pad = n => String(n).padStart(2, '0');
  const toLocalDT = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

  const [recs,    setRecs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [speed,   setSpeed]   = useState(1);   // kept across clips in this popup
  const [fromDT,  setFromDT]  = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1); d.setHours(0, 0, 0, 0); return toLocalDT(d);
  });
  const [toDT,    setToDT]    = useState(() => toLocalDT(new Date()));
  const videoRef = useRef(null);
  const hlsRef   = useRef(null);
  const PER_PAGE = 10;

  // datetime-local value (YYYY-MM-DDTHH:MM) → API format YYYYMMDD_HHMMSS
  const toApiDT = (v) => {
    if (!v) return '';
    const s = v.replace(/[-T:]/g, '');
    return s.slice(0, 8) + '_' + s.slice(8) + '00';
  };

  const load = async (p = 0, from = fromDT, to = toDT) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: PER_PAGE });
      if (camera.streamIdx !== undefined) params.set('cam_idx', camera.streamIdx);
      if (from) params.set('from_dt', toApiDT(from));
      if (to)   params.set('to_dt',   toApiDT(to));
      const r    = await apiFetch(`/folder-recordings?${params}`);
      const data = await r.json();
      setRecs(data.recordings || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch { setRecs([]); setTotal(0); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(0); }, [camera.streamIdx]); // eslint-disable-line

  // Wire up HLS.js whenever an HLS chunk is selected for playback
  useEffect(() => {
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    if (!playing || playing.type !== 'hls' || !videoRef.current) return;
    const url = `${STREAM_API}/hls/${camera.streamIdx}/dvr.m3u8?from_dt=${playing.datetime}&to_dt=${playing.datetime_end || playing.datetime}&token=${streamToken}`;
    const hls = new Hls({ enableWorker: true });
    hlsRef.current = hls;
    hls.loadSource(url);
    hls.attachMedia(videoRef.current);
    hls.on(Hls.Events.MANIFEST_PARSED, () => videoRef.current?.play().catch(() => {}));
    return () => { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } };
  }, [playing]); // eslint-disable-line

  const fmtDT = (dt) => {
    if (!dt || dt.length < 15) return dt || '—';
    return `${dt.slice(6,8)}/${dt.slice(4,6)}/${dt.slice(0,4)}  ${dt.slice(9,11)}:${dt.slice(11,13)}:${dt.slice(13,15)}`;
  };

  const videoUrl = (rec) => rec.type !== 'hls' ? `${STREAM_API}${rec.url}?token=${streamToken}` : null;
  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <div className="camera-modal" onClick={onClose}>
      <div className="camera-modal-content" onClick={e => e.stopPropagation()}
        style={{ maxWidth:620, width:'96vw', maxHeight:'90vh', display:'flex', flexDirection:'column' }}>

        {/* header */}
        <div className="modal-header" style={{ flexShrink:0 }}>
          <div className="modal-title">
            <h2 style={{ fontSize:16 }}>🎞️ {camera.name}</h2>
            <span style={{ fontSize:12, color:'var(--text-muted)' }}>
              {total} recording{total !== 1 ? 's' : ''}
            </span>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* datetime filter */}
        <div style={{ flexShrink:0, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap',
          padding:'10px 16px', borderBottom:'1px solid var(--border)', background:'var(--surface-alt)' }}>
          <span style={{ fontSize:12, color:'var(--text-muted)', fontWeight:500 }}>From</span>
          <input type="datetime-local" value={fromDT} onChange={e => setFromDT(e.target.value)}
            style={{ background:'var(--surface)', border:'1px solid var(--border)', color:'var(--text)',
              padding:'5px 8px', borderRadius:6, fontSize:12 }} />
          <span style={{ fontSize:12, color:'var(--text-muted)' }}>To</span>
          <input type="datetime-local" value={toDT} onChange={e => setToDT(e.target.value)}
            style={{ background:'var(--surface)', border:'1px solid var(--border)', color:'var(--text)',
              padding:'5px 8px', borderRadius:6, fontSize:12 }} />
          <button onClick={() => load(0)}
            style={{ background:'var(--primary)', border:'none', color:'#fff', borderRadius:6,
              padding:'5px 14px', cursor:'pointer', fontSize:12, fontWeight:500 }}>Apply</button>
        </div>

        {/* inline video player */}
        {playing && (
          <div style={{ flexShrink:0, background:'#000', position:'relative' }}>
            <video
              ref={videoRef}
              key={playing.filename || playing.datetime}
              src={videoUrl(playing) || undefined}
              controls autoPlay playsInline
              style={{ width:'100%', maxHeight:260, display:'block' }}
            />
            <PlaybackSpeedButton videoRef={videoRef} speed={speed} onChange={setSpeed}
              mediaKey={playing.filename || playing.datetime}
              style={{ position:'absolute', top:8, right:52, background:'rgba(0,0,0,.7)',
                border:'none', color:'#fff', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:12 }} />
            <button onClick={() => setPlaying(null)}
              style={{ position:'absolute', top:8, right:8, background:'rgba(0,0,0,.7)',
                border:'none', color:'#fff', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:12 }}>
              ✕
            </button>
            <div style={{ padding:'5px 12px', fontSize:11, color:'#888', background:'#111',
              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
              {playing.filename}
            </div>
          </div>
        )}

        {/* list */}
        <div style={{ flex:1, overflowY:'auto', minHeight:0 }}>
          {loading ? (
            <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>
              <div className="connecting-spinner" style={{ margin:'0 auto 14px' }} />
              Loading…
            </div>
          ) : recs.length === 0 ? (
            <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)', fontSize:14 }}>
              No recordings found.
            </div>
          ) : recs.map((rec, i) => {
            const isPlaying = playing?.filename === rec.filename;
            return (
              <div key={rec.filename}
                style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 18px',
                  borderBottom:'1px solid var(--border)',
                  background: isPlaying ? 'rgba(var(--primary-rgb,37,99,235),.08)' : 'transparent',
                  borderLeft: isPlaying ? '3px solid var(--primary)' : '3px solid transparent' }}>

                <div style={{ width:26, height:26, borderRadius:'50%', background:'var(--surface-alt)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:11, fontWeight:600, color:'var(--text-muted)', flexShrink:0 }}>
                  {page * PER_PAGE + i + 1}
                </div>

                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--text)',
                    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginBottom:2 }}>
                    {rec.filename}
                  </div>
                  <div style={{ display:'flex', gap:12, fontSize:12, color:'var(--text-muted)' }}>
                    <span>🕐 {fmtDT(rec.datetime)}</span>
                    <span>💾 {rec.size_mb} MB</span>
                  </div>
                </div>

                <button onClick={() => setPlaying(isPlaying ? null : rec)}
                  style={{ background: isPlaying ? 'var(--primary)' : 'var(--surface-alt)',
                    border: `1px solid ${isPlaying ? 'var(--primary)' : 'var(--border)'}`,
                    color: isPlaying ? '#fff' : 'var(--text)',
                    cursor:'pointer', fontSize:13, padding:'6px 14px',
                    borderRadius:8, flexShrink:0, fontWeight:600 }}>
                  {isPlaying ? '■ Stop' : '▶ Play'}
                </button>
              </div>
            );
          })}
        </div>

        {/* pagination */}
        {totalPages > 1 && (
          <div style={{ flexShrink:0, display:'flex', justifyContent:'space-between', alignItems:'center',
            padding:'10px 16px', borderTop:'1px solid var(--border)', fontSize:12, color:'var(--text-muted)' }}>
            <button className="action-btn" onClick={() => load(page - 1)} disabled={page === 0}
              style={{ width:'auto', padding:'0 14px', opacity: page === 0 ? .3 : 1 }}>‹ Prev</button>
            <span>{page + 1} / {totalPages} &nbsp;·&nbsp; {total} total</span>
            <button className="action-btn" onClick={() => load(page + 1)} disabled={page >= totalPages - 1}
              style={{ width:'auto', padding:'0 14px', opacity: page >= totalPages - 1 ? .3 : 1 }}>Next ›</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// VIEW 1 — Grid
// ═════════════════════════════════════════════════════════════════════════════
function CameraGridView({ cameras, streamToken, phases, onPhaseChange, selectedCamIds }) {
  const [selected, setSelected] = useState(null);
  const [recsCam,  setRecsCam]  = useState(null);
  const [focused,  setFocused]  = useState(null);

  useEffect(() => onFullscreenExit(() => setFocused(null)), []);

  const focusCamera = (cam) => {
    setFocused(cam);
    requestFullscreen();          // best-effort: absent on iOS Safari
  };
  // Clear our own state first — on mobile the browser may never have entered
  // fullscreen, so waiting on the fullscreenchange event would leave us stuck.
  const exitFocused = () => { setFocused(null); exitFullscreen(); };

  const liveStatus = (cam) => {
    const p = phases[cam.id];
    if (p === 'playing') return 'online';
    if (p === 'error')   return 'offline';
    return 'connecting';
  };

  const filtered = cameras.filter(c => selectedCamIds === null || selectedCamIds.has(c.id));

  // The modal/fullscreen portal renders on top of this grid rather than
  // replacing it, so without this every other tile keeps its HLS player
  // running behind the one actually visible — N+1 concurrent streams
  // starving the API's shared threadpool (PRVSK-41). Command view already
  // does the same streamToken={null} trick to release the connection.
  const suspended = !!(selected || focused);

  return (
    <div className="cameras-page">
      {focused && createPortal(
        <div className="wall-focused">
          <VideoPlayer camera={focused} streamToken={streamToken} isLarge />
          <button className="wall-exit-btn" onClick={exitFocused}>✕ Back to Normal</button>
        </div>,
        document.body
      )}
      <main className="cameras-main">
        <div className="camera-container grid">
          <div className="camera-grid">
            {filtered.map(cam => {
              const ls = liveStatus(cam);
              return (
                <div key={cam.id} className={`camera-card ${ls} ${selected?.id === cam.id ? 'selected' : ''}`}
                  onClick={() => ls === 'online' && setSelected(cam)}>
                  <div className="camera-preview">
                    <VideoPlayer camera={cam} streamToken={suspended ? null : streamToken}
                      onPhaseChange={p => onPhaseChange(cam.id, p)} />
                    <button
                      className="wall-cell-expand-btn"
                      title="Fullscreen"
                      onClick={e => { e.stopPropagation(); focusCamera(cam); }}
                    >⛶</button>
                  </div>
                  <div className="camera-info">
                    <div className="camera-header">
                      <h3>
                        {cam.name}
                        {cam.location !== cam.name && <span className="camera-name-zone"> ({cam.location})</span>}
                      </h3>
                      <span className={`status-badge ${ls}`}>{ls}</span>
                    </div>
                    <div className="camera-meta" style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <div style={{ display:'flex', gap:16 }}>
                        <span>📍 {cam.ip}</span>
                      </div>
                      <button className="action-btn" title="Recordings"
                        onClick={e => { e.stopPropagation(); setRecsCam(cam); }}
                        style={{ width:'auto', padding:'0 10px', fontSize:11, fontWeight:500,
                          display:'flex', alignItems:'center', gap:4, height:26 }}>
                        🎞️ Recordings
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {selected && (
          <div className="camera-modal" onClick={() => setSelected(null)}>
            <div className="camera-modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">
                  <h2>{selected.name}</h2>
                  <span className={`status-badge ${selected.status}`}>{selected.status}</span>
                </div>
                <button className="close-btn" onClick={() => setSelected(null)}>✕</button>
              </div>
              <div className="modal-video"><VideoPlayer camera={selected} streamToken={streamToken} isLarge /></div>
              <div className="modal-details">
                <div className="detail-section">
                  <h4>Camera Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item"><label>IP Address</label><span>{selected.ip}</span></div>
                    <div className="detail-item"><label>FPS</label><span>{selected.fps}</span></div>
                    <div className="detail-item"><label>Location</label><span>{selected.location}</span></div>
                  </div>
                </div>
                {selected.type === 'endroid' && <FocusControls camera={selected} />}
              </div>
            </div>
          </div>
        )}

        {recsCam && (
          <RecordingsModal camera={recsCam} streamToken={streamToken} onClose={() => setRecsCam(null)} />
        )}
      </main>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// VIEW 2 — Wall
// ═════════════════════════════════════════════════════════════════════════════
// Selectable page sizes and the grid shape each renders as (cols x rows).
const WALL_LAYOUTS = [1, 4, 9, 12, 16];
const WALL_LAYOUT_COLS = { 1: 1, 4: 2, 9: 3, 12: 4, 16: 4 };
const WALL_LAYOUT_ROWS = { 1: 1, 4: 2, 9: 3, 12: 3, 16: 4 };
// On mobile, cap layouts at 2 columns — 3+ columns leaves each feed too
// narrow to read (PRVSK-14).
const MOBILE_WALL_LAYOUTS = [1, 4];

// Smallest layout that comfortably fits `count` cameras: 1 -> 1-up, 2-4 ->
// 2x2, 5+ -> 3x3 (pagination handles anything past 9). Clamped to whatever
// layouts are actually offered (e.g. mobile tops out at 4).
function bestFitLayout(count, allowed) {
  const desired = count <= 1 ? 1 : count <= 4 ? 4 : 9;
  const candidates = allowed.filter(n => n <= desired);
  return candidates.length ? Math.max(...candidates) : Math.min(...allowed);
}

function CameraWallView({ cameras, streamToken, selectedCamIds, phases }) {
  const { scale } = useSize();
  const isMobile = useIsMobile();
  const wallLayouts = isMobile ? MOBILE_WALL_LAYOUTS : WALL_LAYOUTS;
  const filtered = cameras.filter(c => selectedCamIds === null || selectedCamIds.has(c.id));
  const [focused, setFocused] = useState(null);
  const [layout, setLayout] = useState(() => bestFitLayout(filtered.length, wallLayouts));
  const [page, setPage] = useState(0);
  // Once the user picks a layout pill themselves, stop auto-fitting — their
  // choice sticks even if the camera selection changes afterwards.
  const layoutTouchedRef = useRef(false);

  useEffect(() => onFullscreenExit(() => setFocused(null)), []);

  // Keep the selected layout valid if the viewport crosses the mobile
  // breakpoint (e.g. rotation) while a wider layout was active.
  useEffect(() => {
    if (isMobile && !MOBILE_WALL_LAYOUTS.includes(layout)) { setLayout(4); setPage(0); }
  }, [isMobile]); // eslint-disable-line

  // Auto-fit the grid to however many cameras are actually selected — 1 cam
  // gets 1-up, 2-4 get the 2x2, 5+ get the 3x3 (paginating beyond 9) — until
  // the user manually overrides it via a layout pill.
  useEffect(() => {
    if (layoutTouchedRef.current) return;
    setLayout(bestFitLayout(filtered.length, wallLayouts));
  }, [filtered.length, wallLayouts]); // eslint-disable-line

  const focusCamera = (cam) => {
    setFocused(cam);
    requestFullscreen();          // best-effort: absent on iOS Safari
  };
  const exitFocused = () => { setFocused(null); exitFullscreen(); };

  const totalPages = Math.max(1, Math.ceil(filtered.length / layout));

  // Keep the current page in range when the layout size or camera selection shrinks it.
  useEffect(() => { setPage(p => Math.min(p, totalPages - 1)); }, [totalPages]);

  const pageCameras = filtered.slice(page * layout, page * layout + layout);

  const vhHeight = scale !== 1 ? `calc((100vh - 100px) / ${scale})` : undefined;
  return (
    <div className="wall-view" style={vhHeight ? { height: vhHeight } : undefined}>
      {!focused && (
        <div className="wall-controls">
          <div className="wall-layout-pills">
            {wallLayouts.map(n => (
              <button key={n} className={`wall-layout-pill${layout === n ? ' on' : ''}`}
                onClick={() => { layoutTouchedRef.current = true; setLayout(n); setPage(0); }}>
                {n}
              </button>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="wall-pager">
              <button className="wall-pager-btn" disabled={page === 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}>◀</button>
              <span className="wall-pager-label">Page {page + 1} of {totalPages}</span>
              <button className="wall-pager-btn" disabled={page >= totalPages - 1}
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>▶</button>
            </div>
          )}
        </div>
      )}
      {focused ? createPortal(
        <div className="wall-focused">
          <VideoPlayer camera={focused} streamToken={streamToken} isLarge />
          <button className="wall-exit-btn" onClick={exitFocused}>✕ Back to Normal</button>
        </div>,
        document.body
      ) : (
        <div className={`wall-grid cols-${WALL_LAYOUT_COLS[layout]}${isMobile ? '' : ` rows-${WALL_LAYOUT_ROWS[layout]}`}`}>
          {(isMobile ? pageCameras
            : [...pageCameras, ...Array(Math.max(0, layout - pageCameras.length)).fill(null)]
          ).map((cam, i) => cam ? (
            <div key={cam.id} className="wall-cell" onDoubleClick={() => focusCamera(cam)}>
              <VideoPlayer camera={cam} streamToken={streamToken} wallDegrade />
              <div className="wall-cell-label">{cam.name}</div>
              <button
                className="wall-cell-expand-btn"
                title="Fullscreen"
                onClick={e => { e.stopPropagation(); focusCamera(cam); }}
              >
                ⛶
              </button>
            </div>
          ) : (
            <div key={`empty-${i}`} className="wall-cell empty" />
          ))}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// VIEW 3 — Command
// ═════════════════════════════════════════════════════════════════════════════
function AlertThumb({ filename }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    apiFetch(`/alerts/${encodeURIComponent(filename)}/image`)
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob) { url = URL.createObjectURL(blob); setSrc(url); } })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [filename]);
  return (
    <div className="panel-alert-thumb">
      {src ? <img src={src} alt="" className="panel-alert-thumb-img" /> : '📷'}
    </div>
  );
}

function parseAlertMeta(filename) {
  const m = filename.match(/^(.+?)_cam(\d+)_(\d{8})_(\d{6})/);
  if (!m) return { type: 'unknown', camIdx: null, date: '', time: '' };
  const [, type, camIdxStr, date, time] = m;
  return {
    type,
    camIdx: parseInt(camIdxStr, 10),
    date: `${date.slice(6,8)}/${date.slice(4,6)}/${date.slice(0,4)}`,
    time: `${time.slice(0,2)}:${time.slice(2,4)}`,
  };
}

// Type for the panel filter tabs. The camera-offline row has no <type>_camN_<ts>
// filename, so parseAlertMeta would call it 'unknown' — keep it out of that tab.
const alertType = a => a.is_camera_offline ? 'camoffline' : parseAlertMeta(a.filename).type;

function PanelAlertLightbox({ filename, idx, list, alertTotal, alerts, cameras, TYPE_COLOR, onClose, onNavigate, onAction }) {
  const [src, setSrc] = useState(null);

  const alertsIdx = alerts.findIndex(a => a.filename === filename);
  const alertNum  = alertTotal - alertsIdx;
  const meta      = parseAlertMeta(filename);
  const camName   = meta.camIdx !== null
    ? (cameras.find(c => c.streamIdx === meta.camIdx)?.name || `Cam ${meta.camIdx + 1}`)
    : null;

  useEffect(() => {
    let url;
    setSrc(null);
    apiFetch(`/alerts/${encodeURIComponent(filename)}/image`)
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob) { url = URL.createObjectURL(blob); setSrc(url); } })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [filename]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape')                              onClose();
      if (e.key === 'ArrowRight' && idx < list.length - 1) onNavigate(idx + 1);
      if (e.key === 'ArrowLeft'  && idx > 0)               onNavigate(idx - 1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [idx, list.length, onClose, onNavigate]);

  const navBtn = (dir) => ({
    position:'fixed', top:'50%', transform:'translateY(-50%)',
    [dir === -1 ? 'left' : 'right']: 16,
    background:'rgba(0,0,0,0.55)', border:'1px solid rgba(255,255,255,0.15)',
    borderRadius:'50%', width:44, height:44, color:'#fff', fontSize:20,
    cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
    zIndex:10002, backdropFilter:'blur(4px)',
  });

  return (
    <div style={{ position:'fixed', inset:0, zIndex:10001, background:'rgba(0,0,0,0.88)',
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}
      onClick={onClose}>

      {/* #number */}
      <div style={{ position:'fixed', top:14, left:16, background:'rgba(0,0,0,0.6)',
        color:'#fff', fontSize:13, fontWeight:700, padding:'4px 10px', borderRadius:6, zIndex:10002 }}>
        #{alertNum > 0 ? alertNum : '—'}
      </div>

      {/* Close */}
      <button onClick={onClose}
        style={{ position:'fixed', top:12, right:16, background:'rgba(0,0,0,0.6)',
          border:'1px solid rgba(255,255,255,0.2)', borderRadius:6, color:'#fff',
          fontSize:18, width:34, height:34, cursor:'pointer', zIndex:10002, display:'flex',
          alignItems:'center', justifyContent:'center' }}>✕</button>

      {/* Prev */}
      {idx > 0 && (
        <button style={navBtn(-1)} onClick={e => { e.stopPropagation(); onNavigate(idx - 1); }}>‹</button>
      )}

      {/* Image */}
      {src
        ? <img src={src} alt={filename} onClick={e => e.stopPropagation()}
            style={{ maxWidth:'90vw', maxHeight:'72vh', objectFit:'contain', borderRadius:8, display:'block' }} />
        : <div style={{ color:'#666', fontSize:14 }}>Loading…</div>
      }

      {/* Next */}
      {idx < list.length - 1 && (
        <button style={navBtn(1)} onClick={e => { e.stopPropagation(); onNavigate(idx + 1); }}>›</button>
      )}

      {/* Meta bar */}
      <div onClick={e => e.stopPropagation()}
        style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', justifyContent:'center',
          marginTop:14, padding:'8px 16px', background:'rgba(0,0,0,0.5)', borderRadius:8 }}>
        <span style={{ fontSize:12, fontWeight:700, color: TYPE_COLOR[meta.type] || '#fff', textTransform:'capitalize' }}>
          {meta.type === 'instant' ? 'Detected' : meta.type}
        </span>
        {camName && <span style={{ fontSize:12, color:'#ccc' }}>{camName}</span>}
        <span style={{ fontSize:12, color:'#aaa' }}>{meta.date} {meta.time}</span>
        <span style={{ fontSize:11, color:'#666' }}>{idx + 1} / {list.length}</span>
        {meta.type === 'unknown' && (
          <>
            {productHas('known-faces-add') && <button onClick={() => onAction({ filename, mode: 'known' })}
              style={{ fontSize:11, padding:'3px 9px', borderRadius:5, border:'1px solid rgba(39,174,96,0.5)',
                background:'transparent', color:'#27ae60', cursor:'pointer' }}>👤 Known</button>}
            {productHas('banned') && <button onClick={() => onAction({ filename, mode: 'ban' })}
              style={{ fontSize:11, padding:'3px 9px', borderRadius:5, border:'1px solid rgba(231,76,60,0.5)',
                background:'transparent', color:'#e74c3c', cursor:'pointer' }}>🚫 Ban</button>}
          </>
        )}
      </div>
    </div>
  );
}

function CamFilterDropdown({ cameras, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const options = [{ label: 'All Cameras', value: null, icon: '📷' },
    ...cameras.map(c => ({ label: c.name, value: c.streamIdx, icon: '🎥', sub: `Cam ${c.streamIdx + 1}` }))];

  const selected = options.find(o => o.value === value) || options[0];

  return (
    <div className="pcf-wrap" ref={ref}>
      <button className={`pcf-trigger${open ? ' open' : ''}`} onClick={() => setOpen(o => !o)}>
        <span className="pcf-icon">{selected.icon}</span>
        <span className="pcf-label">{selected.label}</span>
        {selected.sub && <span className="pcf-sub">{selected.sub}</span>}
        <svg className="pcf-arrow" width="10" height="10" viewBox="0 0 10 10">
          <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      </button>
      {open && (
        <div className="pcf-menu">
          {options.map(o => (
            <button
              key={String(o.value)}
              className={`pcf-item${o.value === value ? ' active' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              <span className="pcf-item-icon">{o.icon}</span>
              <span className="pcf-item-label">{o.label}</span>
              {o.sub && <span className="pcf-item-sub">{o.sub}</span>}
              {o.value === value && <span className="pcf-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PanelActionModal({ filename, mode, onClose, onDone }) {
  // step: 'loading' | 'pick' | 'new'
  const [step,    setStep]    = useState('loading');
  const [persons, setPersons] = useState([]);
  const [name,    setName]    = useState('');
  const [profile, setProfile] = useState('day');
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');
  const inputRef = useRef(null);

  const isKnown = mode === 'known';
  const accentColor = isKnown ? '#27ae60' : '#e74c3c';

  // Load existing contacts on mount
  useEffect(() => {
    apiFetch(isKnown ? '/faces/known' : '/faces/banned')
      .then(r => r.ok ? r.json() : { persons: [] })
      .then(d => {
        setPersons((d.persons || []).map(p => p.name).sort());
        setStep('pick');
      })
      .catch(() => setStep('pick'));
  }, [isKnown]);

  // Auto-focus name input when switching to 'new'
  useEffect(() => {
    if (step === 'new') inputRef.current?.focus();
  }, [step]);

  const avatarColors = ['#2ecc71','#3498db','#9b59b6','#e67e22','#1abc9c','#e91e63','#00bcd4','#ff5722'];
  const avatarColor = (n) => {
    let h = 0;
    for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffffffff;
    return avatarColors[Math.abs(h) % avatarColors.length];
  };

  const enroll = async (targetName) => {
    setBusy(true); setErr('');
    try {
      if (isKnown) {
        await apiFetch(`/faces/known/${encodeURIComponent(targetName)}/from_alert?filename=${encodeURIComponent(filename)}&profile=${profile}`, { method: 'POST' });
        onDone(`Added to ${targetName}`);
      } else {
        await apiFetch(`/faces/banned/${encodeURIComponent(targetName)}/from_alert?filename=${encodeURIComponent(filename)}`, { method: 'POST' });
        onDone(`${targetName} banned`);
      }
    } catch {
      setErr('Request failed');
      setBusy(false);
    }
  };

  const handleNewSubmit = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) { setErr('Enter a name'); return; }
    await enroll(n);
  };

  return (
    <div className="pk-lightbox" onClick={onClose}
      style={{ padding: '24px 16px', overflowY: 'auto', alignItems: 'center', justifyContent: 'flex-start' }}>
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14,
                    padding:22, width:'100%', maxWidth:340,
                    maxHeight:'calc(100vh - 48px)', overflowY:'auto', flexShrink:0 }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ margin:0, fontSize:14, color:'var(--text)' }}>
            {step === 'new'
              ? (isKnown ? '👤 New Person' : '🚫 New Entry')
              : (isKnown ? '👤 Mark as Known' : '🚫 Ban Person')}
          </h3>
          <button className="pk-lightbox-close" style={{ position:'static' }} onClick={onClose}>✕</button>
        </div>

        {/* Loading */}
        {step === 'loading' && (
          <div style={{ textAlign:'center', padding:'18px 0', color:'var(--text-muted)', fontSize:12 }}>
            ⏳ Loading contacts…
          </div>
        )}

        {/* Pick step */}
        {step === 'pick' && (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {persons.length > 0 && (
              <>
                <p style={{ margin:'0 0 2px', fontSize:10, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Add to existing
                </p>
                <div style={{ display:'flex', flexDirection:'column', gap:5, maxHeight:200, overflowY:'auto' }}>
                  {persons.map(pName => (
                    <button key={pName} disabled={busy} onClick={() => enroll(pName)}
                      style={{ display:'flex', alignItems:'center', gap:9,
                        background:'var(--surface-alt, rgba(255,255,255,0.04))',
                        border:'1px solid var(--border)', borderRadius:8,
                        padding:'8px 12px', cursor:'pointer', color:'var(--text)',
                        fontSize:13, textAlign:'left', width:'100%' }}>
                      <span style={{ width:26, height:26, borderRadius:'50%', flexShrink:0,
                        background: avatarColor(pName),
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:11, fontWeight:600, color:'#fff' }}>
                        {pName.charAt(0).toUpperCase()}
                      </span>
                      {pName}
                    </button>
                  ))}
                </div>

                {/* Profile selector shown in pick step for known mode */}
                {isKnown && (
                  <div style={{ display:'flex', gap:6, marginTop:2 }}>
                    {['day','night'].map(p => (
                      <button key={p} type="button"
                        className={`pk-filter-tab${profile === p ? ' active' : ''}`}
                        onClick={() => setProfile(p)}>
                        {p === 'day' ? '☀️ Day' : '🌙 Night'}
                      </button>
                    ))}
                  </div>
                )}

                <div style={{ height:1, background:'var(--border)', margin:'2px 0' }} />
              </>
            )}

            {err && <div style={{ fontSize:11, color:'#e74c3c' }}>{err}</div>}
            {busy && <div style={{ fontSize:11, color:'var(--text-muted)', textAlign:'center' }}>⏳ Enrolling…</div>}

            <button onClick={() => { setErr(''); setStep('new'); }}
              style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                background:'transparent', border:`1px dashed ${accentColor}`,
                borderRadius:8, padding:'8px 12px', cursor:'pointer',
                color: accentColor, fontSize:13, fontWeight:600 }}>
              ➕ New Person
            </button>
          </div>
        )}

        {/* New person step */}
        {step === 'new' && (
          <form onSubmit={handleNewSubmit} style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {persons.length > 0 && (
              <button type="button" onClick={() => { setStep('pick'); setErr(''); }}
                style={{ alignSelf:'flex-start', background:'transparent', border:'none',
                  color:'var(--text-muted)', cursor:'pointer', fontSize:12, padding:0 }}>
                ← Back
              </button>
            )}
            <input ref={inputRef} className="alert-inline-input"
              style={{ padding:'8px 12px', fontSize:13 }}
              placeholder="Person name…" value={name}
              onChange={e => setName(e.target.value)} />
            {isKnown && (
              <div style={{ display:'flex', gap:6 }}>
                {['day','night'].map(p => (
                  <button key={p} type="button"
                    className={`pk-filter-tab${profile === p ? ' active' : ''}`}
                    onClick={() => setProfile(p)}>
                    {p === 'day' ? '☀️ Day' : '🌙 Night'}
                  </button>
                ))}
              </div>
            )}
            {err && <div style={{ fontSize:11, color:'#e74c3c' }}>{err}</div>}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button type="button" className="pk-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className={`pk-btn ${isKnown ? 'success' : 'danger'}`} disabled={busy}>
                {busy ? '⏳…' : isKnown ? '✓ Enroll' : '🚫 Ban'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function AlertLogModal({ data, filename, onClose }) {
  const fmt = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts * 1000);
    return d.toLocaleString();
  };
  const pct = (v) => v != null ? `${Math.round(v * 100)}%` : '—';
  const t = data.telemetry || {};

  const rows = [
    { label: 'Alert ID',      value: data.id },
    { label: 'Time',          value: fmt(data.ts) },
    { label: 'Camera',        value: data.cam_name || '—' },
    { label: 'Type',          value: data.label || '—' },
    { label: 'Verdict',       value: data.verdict || 'Pending' },
  ];

  const telRows = [
    t.face_name && t.face_name !== 'Unknown' && { label: 'Face Match',    value: `${t.face_name} (${pct(t.face_score)})` },
    t.ban_name  && t.ban_name  !== 'Unknown' && { label: 'Ban Match',     value: `${t.ban_name} (${pct(t.banned_score)})` },
    t.in_frame_secs != null                  && { label: 'In Frame',      value: `${Number(t.in_frame_secs).toFixed(1)}s` },
    t.face_score    != null                  && { label: 'Face Score',    value: pct(t.face_score) },
    t.banned_score  != null                  && { label: 'Ban Score',     value: pct(t.banned_score) },
    t.skeleton_score!= null                  && { label: 'Skeleton',      value: pct(t.skeleton_score) },
    t.ev_face_large != null                  && { label: 'Large Face',    value: t.ev_face_large ? '✅ Yes' : '❌ No' },
    t.ev_skeleton   != null                  && { label: 'Skeleton Det.', value: t.ev_skeleton   ? '✅ Yes' : '❌ No' },
    t.ev_clip_human != null                  && { label: 'CLIP Human',    value: t.ev_clip_human ? '✅ Yes' : '❌ No' },
    t.alert_rule                             && { label: 'Rule',          value: t.alert_rule },
    t.timer         != null                  && { label: 'Timer',         value: `${Number(t.timer).toFixed(1)}s` },
  ].filter(Boolean);

  const download = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace(/\.(jpg|jpeg|png)$/i, '')}_log.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 16 }}>
      <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</p>
      {children}
    </div>
  );

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0',
      borderBottom: '1px solid var(--border)', alignItems: 'flex-start' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0, width: 110 }}>{label}</span>
      <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500, wordBreak: 'break-all' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10002, background: 'rgba(0,0,0,0.65)',
      backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
          padding: 24, width: '100%', maxWidth: 460, maxHeight: '85vh',
          overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: 'var(--text)' }}>📋 Alert Log</h3>
          <button onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)',
              fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        <Section title="Summary">
          {rows.map(r => <Row key={r.label} label={r.label} value={r.value} />)}
        </Section>

        {telRows.length > 0 && (
          <Section title="Detection Evidence">
            {telRows.map(r => <Row key={r.label} label={r.label} value={r.value} />)}
          </Section>
        )}

        {data.false_reason && (
          <Section title="False Alert Reason">
            <Row label="Reason" value={data.false_reason} />
          </Section>
        )}

        {/* Download button */}
        <button onClick={download}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', marginTop: 8, padding: '9px 0',
            background: 'var(--surface-alt)', border: '1px solid var(--border)',
            borderRadius: 8, color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          ⬇️ Download Full JSON
        </button>
      </div>
    </div>
  );
}

function CameraCommandView({ cameras, streamToken, phases, onPhaseChange }) {
  const { scale } = useSize();
  const [selected,      setSelected]      = useState(cameras[0] || null);
  const [alerts,        setAlerts]        = useState([]);
  const [alertTotal,    setAlertTotal]    = useState(0);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsFetching,setAlertsFetching]= useState(false);
  const [alertHasMore,  setAlertHasMore]  = useState(false);
  const [alertFilter,   setAlertFilter]   = useState('all');
  const [alertCamFilter,setAlertCamFilter]= useState(null); // null = all cameras
  const [actionModal,   setActionModal]   = useState(null); // { filename, mode }
  const [panelToast,    setPanelToast]    = useState(null);
  const [panelLightbox, setPanelLightbox] = useState(null); // { filename, idx, list }
  const [camOffModal,   setCamOffModal]   = useState(null); // is_camera_offline alert row
  const [logModal,      setLogModal]      = useState(null); // { data, filename }

  const commandMainRef     = useRef(null);
  const [commandFs, setCommandFs] = useState(false);
  const alertSentinelRef   = useRef(null);
  const alertFetchingRef   = useRef(false);
  const alertPageRef       = useRef(0);
  const alertHasMoreRef    = useRef(false);
  const alertCamFilterRef  = useRef(null);
  const alertLatestIdRef   = useRef(null);   // updated after every successful load
  const alertLatestTotRef  = useRef(0);
  const lastScrollRef      = useRef(Date.now()); // timestamp of last scroll in alerts list
  const idleReloadTimerRef = useRef(null);   // timeout id for idle-triggered reload
  const [alertBanner, setAlertBanner] = useState(null); // null | { added: number }

  const showPanelToast = (msg) => {
    setPanelToast(msg);
    setTimeout(() => setPanelToast(null), 2500);
  };


  const openAlertLog = async (filename, e) => {
    e?.stopPropagation();
    try {
      const res  = await apiFetch(`/alerts/${encodeURIComponent(filename)}/info`);
      const data = await res.json();
      setLogModal({ data, filename });
    } catch { showPanelToast('No log info for this alert'); }
  };

  const liveStatus = (cam) => {
    const p = phases[cam.id];
    if (p === 'playing') return 'online';
    if (p === 'error')   return 'offline';
    return 'connecting';
  };

  // Command has its own camera picker (the left sidebar below) — unlike Wall
  // and Grid it never filters by the top toolbar's camera chips, so switching
  // into this tab always shows every camera regardless of what's selected
  // elsewhere.
  const filtered = cameras;

  useEffect(() => {
    if (!selected && filtered.length > 0) setSelected(filtered[0]);
  }, [filtered.length]);

  useEffect(() => onFullscreenExit(() => setCommandFs(false)), []);

  const loadAlerts = (p, reset = false, camFilter = alertCamFilterRef.current) => {
    if (alertFetchingRef.current) return;
    alertFetchingRef.current = true;
    if (reset) setAlertsLoading(true);
    else       setAlertsFetching(true);
    const camParam = camFilter !== null ? `&cam=${camFilter}` : '';
    apiFetch(`/ui/alerts?page=${p}&per_page=50&exclude=snapshot${camParam}`)
      .then(r => r.json())
      .then(d => {
        const items = d.alerts || [];
        setAlerts(prev => reset ? items : [...prev, ...items]);
        if (d.total != null) setAlertTotal(d.total);
        alertPageRef.current      = p;
        alertLatestIdRef.current  = items[0]?.filename ?? null;
        alertLatestTotRef.current = d.total || 0;
        const more = items.length === 50;
        alertHasMoreRef.current = more;
        setAlertHasMore(more);
      })
      .catch(() => { if (reset) setAlerts([]); })
      .finally(() => {
        alertFetchingRef.current = false;
        setAlertsLoading(false);
        setAlertsFetching(false);
      });
  };

  // Initial load — all cameras
  useEffect(() => {
    alertPageRef.current    = 0;
    alertHasMoreRef.current = false;
    loadAlerts(0, true, null);
  }, []); // eslint-disable-line

  // Reload when camera filter changes
  useEffect(() => {
    alertCamFilterRef.current = alertCamFilter;
    alertPageRef.current      = 0;
    alertHasMoreRef.current   = false;
    loadAlerts(0, true, alertCamFilter);
  }, [alertCamFilter]); // eslint-disable-line

  // Silent background poll — shows banner, auto-reloads after idle timeout
  useEffect(() => {
    const ms = getAlertPollMs();
    if (!ms) return;

    const scheduleIdleReload = () => {
      if (idleReloadTimerRef.current) clearTimeout(idleReloadTimerRef.current);
      const idleMs = getIdleReloadMs();
      if (!idleMs) return; // "Never" — user must tap banner
      const check = () => {
        const idleSince = Date.now() - lastScrollRef.current;
        if (idleSince >= idleMs) {
          setAlertBanner(null);
          idleReloadTimerRef.current = null;
          lastScrollRef.current = Date.now();
          loadAlerts(0, true, alertCamFilterRef.current);
        } else {
          idleReloadTimerRef.current = setTimeout(check, idleMs - idleSince);
        }
      };
      idleReloadTimerRef.current = setTimeout(check, idleMs);
    };

    const silentCheck = async () => {
      if (document.visibilityState !== 'visible') return;
      if (alertFetchingRef.current) return;
      try {
        const camParam = alertCamFilterRef.current !== null ? `&cam=${alertCamFilterRef.current}` : '';
        const r = await apiFetch(`/ui/alerts?page=0&per_page=1&exclude=snapshot${camParam}`);
        const d = await r.json();
        const newTotal  = d.total || 0;
        const newLatest = (d.alerts || [])[0]?.filename ?? null;
        if (newTotal !== alertLatestTotRef.current || newLatest !== alertLatestIdRef.current) {
          const added = Math.max(0, newTotal - alertLatestTotRef.current);
          alertLatestIdRef.current  = newLatest;
          alertLatestTotRef.current = newTotal;
          const idleMs = getIdleReloadMs();
          const idleSince = Date.now() - lastScrollRef.current;
          if (idleMs && idleSince >= idleMs) {
            setAlertBanner(null);
            lastScrollRef.current = Date.now();
            loadAlerts(0, true, alertCamFilterRef.current);
          } else {
            setAlertBanner(prev => ({ added: (prev?.added || 0) + (added || 1) }));
            scheduleIdleReload();
          }
        }
      } catch { /* silent */ }
    };

    let id = null;
    const start = () => { if (!id) id = setInterval(silentCheck, ms); };
    const stop  = () => { clearInterval(id); id = null; };
    const onVis = () => document.visibilityState === 'visible' ? start() : stop();
    document.addEventListener('visibilitychange', onVis);
    start();
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      if (idleReloadTimerRef.current) clearTimeout(idleReloadTimerRef.current);
    };
  }, []); // eslint-disable-line

  // IntersectionObserver for panel alerts list
  useEffect(() => {
    const el = alertSentinelRef.current;
    if (!el || !alertHasMore || alertsFetching || alertsLoading) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && alertHasMoreRef.current && !alertFetchingRef.current) {
        loadAlerts(alertPageRef.current + 1, false);
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [alertHasMore, alertsFetching, alertsLoading]); // eslint-disable-line

  const updatePhase = (id, p) => onPhaseChange(id, p);

  const TYPE_COLOR = { banned:'#e74c3c', instant:'#3498db', motion:'#f39c12', unknown:'#888' };

  return (
    <div className="command-view" style={scale !== 1 ? { height: `calc((100vh - 100px) / ${scale})` } : undefined}>
      <aside className="command-sidebar">
        <div className="command-sidebar-header">Cameras</div>
        {filtered.map(cam => {
          const ls = liveStatus(cam);
          return (
            <div key={cam.id} className={`command-cam-item${selected?.id === cam.id ? ' active' : ''}`}
              onClick={() => setSelected(cam)}>
              <div className="command-cam-thumb">
                <SnapshotThumb camera={cam} streamToken={streamToken}
                  onPhaseChange={p => updatePhase(cam.id, p)} />
              </div>
              <div className="command-cam-meta">
                <span className="command-cam-name">{cam.name}</span>
                <span className={`command-cam-status ${ls}`}>{ls}</span>
              </div>
            </div>
          );
        })}
      </aside>

      {commandFs && selected && createPortal(
        <div className="wall-focused">
          <VideoPlayer camera={selected} streamToken={streamToken} isLarge
            onPhaseChange={p => updatePhase(selected.id, p)} />
          <button className="wall-exit-btn"
            onClick={() => { setCommandFs(false); exitFullscreen(); }}>✕ Back to Normal</button>
        </div>,
        document.body
      )}
      <div className="command-main" ref={commandMainRef}>
        {selected
          ? <>
              <VideoPlayer camera={selected} streamToken={commandFs ? null : streamToken} isLarge
                onPhaseChange={p => updatePhase(selected.id, p)} />
              <button className="wall-cell-expand-btn" title="Fullscreen"
                onClick={() => { setCommandFs(true); requestFullscreen(); }}>⛶</button>
            </>
          : <div className="command-empty">Select a camera</div>
        }
      </div>

      <aside className="command-panel">

        {/* ── Recent Alerts ── */}
        <div className="command-panel-section" style={{ flex: 1, display:'flex', flexDirection:'column', minHeight:0 }}>
          <div className="command-panel-header">
            <span>Recent Alerts</span>
            {alertTotal > 0 && <span className="panel-alert-count">{alertTotal}</span>}
          </div>

          {/* Camera filter */}
          <CamFilterDropdown
            cameras={cameras}
            value={alertCamFilter}
            onChange={setAlertCamFilter}
          />

          {/* Type filter tabs */}
          <div className="panel-alert-filters">
            {['all','unknown','banned','instant','motion'].map(f => {
              const count = f === 'all' ? alertTotal : alerts.filter(a => alertType(a) === f).length;
              const label = f === 'all' ? `All (${count})` : f === 'instant' ? 'Detected' : f.charAt(0).toUpperCase() + f.slice(1);
              return (
                <button key={f} className={`panel-alert-filter-btn${alertFilter === f ? ' active' : ''}`}
                  onClick={() => setAlertFilter(f)}>
                  {label}
                </button>
              );
            })}
          </div>

          {alertBanner && (
            <button
              onClick={() => {
                setAlertBanner(null);
                if (idleReloadTimerRef.current) { clearTimeout(idleReloadTimerRef.current); idleReloadTimerRef.current = null; }
                loadAlerts(0, true, alertCamFilterRef.current);
              }}
              style={{ display:'block', width:'100%', padding:'7px 12px', margin:'4px 0 2px',
                background:'var(--banner-bg)', color:'var(--banner-text)', border:'none',
                borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600, textAlign:'center' }}>
              {alertBanner.added > 0
                ? `${alertBanner.added} new alert${alertBanner.added !== 1 ? 's' : ''} — tap to refresh`
                : 'New alerts — tap to refresh'}
            </button>
          )}

          <div className="panel-alerts-list" onScroll={() => { lastScrollRef.current = Date.now(); }}>
            {alertsLoading ? (
              <p className="command-panel-empty">Loading…</p>
            ) : (() => {
              const visible = alertFilter === 'all' ? alerts : alerts.filter(a => alertType(a) === alertFilter);
              // The lightbox pages through images only — the offline row has none.
              const imageList = visible.filter(a => !a.is_camera_offline);
              if (visible.length === 0) return <p className="command-panel-empty">No alerts for this filter</p>;
              return (
                <>
                  {visible.map(a => {
                    if (a.is_camera_offline) {
                      const t = a.ts || '';
                      const when = t.length >= 15 ? `${t.slice(6,8)}/${t.slice(4,6)}/${t.slice(0,4)} ${t.slice(9,11)}:${t.slice(11,13)}` : '';
                      const alertNum = alertTotal - alerts.findIndex(x => x.filename === a.filename);
                      return (
                        <div key={a.filename} className="panel-alert-item" style={{ alignItems:'flex-start', cursor:'pointer' }}
                          onClick={() => setCamOffModal(a)}>
                          <div style={{ position:'relative', flexShrink:0 }}>
                            <div className="panel-alert-thumb" style={{ display:'flex', alignItems:'center', justifyContent:'center',
                              background:'#111', fontSize:22 }}>📴</div>
                            <span style={{ position:'absolute', top:3, left:3, background:'rgba(0,0,0,0.6)',
                              color:'#fff', fontSize:9, fontWeight:700, padding:'1px 4px', borderRadius:3, lineHeight:1.4 }}>
                              #{alertNum}
                            </span>
                          </div>
                          <div className="panel-alert-info">
                            <span className="panel-alert-type" style={{ color:'#e74c3c' }}>{a.label}</span>
                            {a.cameras?.length === 1 && <span className="panel-alert-cam">{a.cameras[0].cam_name}</span>}
                            <span className="panel-alert-time">{when}</span>
                            <div style={{ display:'flex', gap:4, marginTop:4 }}>
                              <button title="Which cameras are offline, and for how long"
                                style={{ fontSize:10, padding:'2px 8px', borderRadius:5, border:'1px solid rgba(100,100,100,0.3)', background:'transparent', color:'var(--text-muted)', cursor:'pointer', whiteSpace:'nowrap', marginLeft:'auto' }}
                                onClick={e => { e.stopPropagation(); setCamOffModal(a); }}>
                                ℹ️ Details
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    const meta = parseAlertMeta(a.filename);
                    const camName = meta.camIdx !== null
                      ? (cameras.find(c => c.streamIdx === meta.camIdx)?.name || `Cam ${meta.camIdx + 1}`)
                      : null;
                    const alertsIdx = alerts.findIndex(x => x.filename === a.filename);
                    const alertNum  = alertTotal - alertsIdx;
                    return (
                      <div key={a.filename} className="panel-alert-item" style={{ alignItems:'flex-start', cursor:'pointer' }}
                        onClick={() => setPanelLightbox({ filename: a.filename, idx: imageList.indexOf(a), list: imageList })}>
                        <div style={{ position:'relative', flexShrink:0 }}>
                          <AlertThumb filename={a.filename} />
                          <span style={{ position:'absolute', top:3, left:3, background:'rgba(0,0,0,0.6)',
                            color:'#fff', fontSize:9, fontWeight:700, padding:'1px 4px', borderRadius:3, lineHeight:1.4 }}>
                            #{alertNum}
                          </span>
                        </div>
                        <div className="panel-alert-info">
                          <span className="panel-alert-type" style={{ color: TYPE_COLOR[meta.type] || 'var(--text)' }}>
                            {meta.type === 'instant' ? 'Detected' : meta.type.charAt(0).toUpperCase() + meta.type.slice(1)}
                          </span>
                          {camName && <span className="panel-alert-cam">{camName}</span>}
                          <span className="panel-alert-time">{meta.date} {meta.time}</span>
                          <div style={{ display:'flex', gap:4, marginTop:4 }}>
                            {meta.type === 'unknown' && <>
                              <button title="Mark as Known"
                                style={{ fontSize:10, padding:'2px 8px', borderRadius:5, border:'1px solid rgba(39,174,96,0.4)', background:'transparent', color:'#27ae60', cursor:'pointer', whiteSpace:'nowrap' }}
                                onClick={e => { e.stopPropagation(); setActionModal({ filename: a.filename, mode: 'known' }); }}>
                                👤 Known
                              </button>
                              <button title="Ban person"
                                style={{ fontSize:10, padding:'2px 8px', borderRadius:5, border:'1px solid rgba(231,76,60,0.4)', background:'transparent', color:'#e74c3c', cursor:'pointer', whiteSpace:'nowrap' }}
                                onClick={e => { e.stopPropagation(); setActionModal({ filename: a.filename, mode: 'ban' }); }}>
                                🚫 Ban
                              </button>
                            </>}
                            <button title="Download alert log (JSON)"
                              style={{ fontSize:10, padding:'2px 8px', borderRadius:5, border:'1px solid rgba(100,100,100,0.3)', background:'transparent', color:'var(--text-muted)', cursor:'pointer', whiteSpace:'nowrap', marginLeft:'auto' }}
                              onClick={e => openAlertLog(a.filename, e)}>
                              ℹ️ Info
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={alertSentinelRef} style={{ height: 1 }} />
                  {alertsFetching && <p className="command-panel-empty">Loading more…</p>}
                </>
              );
            })()}
          </div>
        </div>

      </aside>

      {camOffModal && <CamOfflineModal alert={camOffModal} onClose={() => setCamOffModal(null)} />}

      {panelLightbox && (
        <PanelAlertLightbox
          filename={panelLightbox.filename}
          idx={panelLightbox.idx}
          list={panelLightbox.list}
          alertTotal={alertTotal}
          alerts={alerts}
          cameras={cameras}
          TYPE_COLOR={TYPE_COLOR}
          onClose={() => setPanelLightbox(null)}
          onNavigate={(newIdx) => setPanelLightbox(prev => ({
            ...prev, idx: newIdx, filename: prev.list[newIdx].filename,
          }))}
          onAction={(modal) => { setPanelLightbox(null); setActionModal(modal); }}
        />
      )}

      {actionModal && (
        <PanelActionModal
          filename={actionModal.filename}
          mode={actionModal.mode}
          onClose={() => setActionModal(null)}
          onDone={(msg) => { setActionModal(null); showPanelToast(msg); }}
        />
      )}

      {logModal && (
        <AlertLogModal
          data={logModal.data}
          filename={logModal.filename}
          onClose={() => setLogModal(null)}
        />
      )}

      {panelToast && (
        <div className="pk-toast ok" style={{ position:'fixed', bottom:24, right:24, zIndex:10000 }}>
          {panelToast}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Loading placeholder
// ═════════════════════════════════════════════════════════════════════════════
function CamerasLoading() {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'60vh', gap:16, color:'var(--text-secondary, #888)' }}>
      <div className="connecting-spinner" />
      <span>Loading cameras...</span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Main Page
// ═════════════════════════════════════════════════════════════════════════════
export default function MyCamerasPage() {
  const isMobile = useIsMobile();
  // Mobile defaults to Command View (Wall's multi-tile grid doesn't fit a
  // phone screen usefully); desktop keeps its existing Wall default. isMobile
  // is already resolved synchronously from window.innerWidth on first render
  // (see useIsMobile), so this lazy initializer never flashes the wrong tab.
  const [activeTab,    setActiveTab]    = useState(() => isMobile ? 'command' : 'wall');
  const [streamToken,  setStreamToken]  = useState(null);
  const [cameras,      setCameras]      = useState([]);
  const [loadingCams,  setLoadingCams]  = useState(true);
  const [reloading,    setReloading]    = useState(false);
  const [phases,         setPhases]         = useState({});
  const [selectedCamIds, setSelectedCamIds] = useState(null); // null = all
  const [isFs,         setIsFs]         = useState(false);
  const rootRef = useRef(null);

  const onPhaseChange = (id, p) => setPhases(prev => ({ ...prev, [id]: p }));

  const init = async (reloadConfig = false) => {
    if (reloadConfig) {
      setReloading(true);   // button spinner only — keep camera tiles alive
    } else {
      // Initial page load: reset everything and show loading screen
      setLoadingCams(true);
      setPhases({});
    }

    // Step 1+2: obtain auth token via shared streamApi (handles LAN/WAN routing)
    const token = await getToken();
    if (!token) { setLoadingCams(false); setReloading(false); return; }
    // On initial load update token only if it changed — keeps stream alive on navigation back
    if (!reloadConfig) setStreamToken(prev => prev === token ? prev : token);

    // Reload config.json only when explicitly requested (Reload button)
    if (reloadConfig) {
      try { await apiFetch('/settings/reload', { method: 'POST' }); } catch {}
    }

    // Step 3: fetch camera list
    try {
      const res = await apiFetch('/cameras');
      if (res.ok) {
        const data = await res.json();
        // Map first (idx comes from the camera's own `idx` field, not array position —
        // a role restricted to a subset of cameras gets a shorter array whose positions
        // no longer line up with real CAMERA_SOURCES indices), then drop disabled
        // cameras so they don't render as perpetual "Connecting" tiles.
        setCameras((data.cameras || [])
          .map(cam => mapCamera(cam, cam.idx, data.device))
          .filter(c => c.enabled));
        // Set token after cameras are ready — streams reconnect only if token actually changed
        if (token && reloadConfig) setStreamToken(token);
      }
    } catch {} finally {
      setLoadingCams(false);
      setReloading(false);
    }
  };

  useEffect(() => { init(false); }, []);

  useEffect(() => onFullscreenExit(() => setIsFs(false)), []);

  const toggleWallFullscreen = () => {
    if (isFs) {
      setIsFs(false);
      exitFullscreen();
    } else {
      setActiveTab('wall');
      setIsFs(true);
      requestFullscreen();        // best-effort: absent on iOS Safari
    }
  };

  // Clicking a camera chip solos it (PRVSK-35 — a click used to remove that
  // camera from an implicit "everything" set, the opposite of what it looks
  // like it does). From "All", the first click shows just that camera;
  // further clicks add more; clicking an already-selected one removes it.
  // Selecting everything back collapses to null ("All") again.
  const toggleCamId = (id) => {
    setSelectedCamIds(prev => {
      if (prev === null) return new Set([id]);
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      // Deselecting the last camera, or selecting everything back, both
      // collapse to "All" rather than leaving an empty selection.
      if (next.size === 0 || next.size === cameras.length) return null;
      return next;
    });
  };

  const sidebarControls = (
    <div className="page-toolbar">
      {[{id:'command',label:'Command',icon:'🎛'},{id:'grid',label:'Grid View',icon:'⊞'},{id:'wall',label:'Wall',icon:'▦'}].map(t => (
        <button key={t.id} className={`tb-btn${activeTab === t.id ? ' active' : ''}`} onClick={() => setActiveTab(t.id)}>
          {t.icon} {t.label}
        </button>
      ))}
      {/* Camera filter chips — Wall/Grid only. Command has its own camera
          picker (the left sidebar), so these would just be a second,
          differently-behaved way to do the same thing there. */}
      {activeTab !== 'command' && (
        <>
          <div className="tb-sep" />
          <button className={`tb-btn${selectedCamIds === null ? ' active' : ''}`} onClick={() => setSelectedCamIds(null)}>
            All
          </button>
          {cameras.map(cam => (
            <button key={cam.id}
              className={`tb-btn${selectedCamIds?.has(cam.id) ? ' active' : ''}`}
              onClick={() => toggleCamId(cam.id)}>
              {cam.name}
            </button>
          ))}
        </>
      )}
      <div className="tb-sep" />
      <button className="tb-ghost-btn" onClick={() => init(true)} disabled={reloading}>
        {reloading ? '⏳' : '🔄'} Reload
      </button>
      <div className="tb-sep" />
      <button className="tb-ghost-btn" onClick={toggleWallFullscreen} title="Wall fullscreen">
        {isFs ? '✕ Back to Normal' : '⛶ Wall Fullscreen'}
      </button>
    </div>
  );

  if (loadingCams) {
    return (
      <div className="cameras-root">
        {sidebarControls}
        <CamerasLoading />
      </div>
    );
  }

  return (
    <div className={`cameras-root${activeTab === 'wall' ? ' cameras-root-wall' : ''}`} ref={rootRef}>
      {sidebarControls}
      {isFs && createPortal(
        <div className="cameras-root-fs">
          <CameraWallView cameras={cameras} streamToken={streamToken} selectedCamIds={selectedCamIds} phases={phases} />
          <button className="wall-exit-btn" onClick={toggleWallFullscreen}>✕ Back to Normal</button>
        </div>,
        document.body
      )}
      <div style={{ display: activeTab === 'grid'    ? 'contents' : 'none' }}><CameraGridView    cameras={cameras} streamToken={activeTab === 'grid'    ? streamToken : null} phases={phases} onPhaseChange={onPhaseChange} selectedCamIds={selectedCamIds} /></div>
      <div style={{ display: activeTab === 'wall'    ? 'contents' : 'none' }}><CameraWallView    cameras={cameras} streamToken={activeTab === 'wall' && !isFs ? streamToken : null} selectedCamIds={selectedCamIds} phases={phases} /></div>
      <div style={{ display: activeTab === 'command' ? 'contents' : 'none' }}><CameraCommandView cameras={cameras} streamToken={activeTab === 'command' ? streamToken : null} phases={phases} onPhaseChange={onPhaseChange} /></div>
    </div>
  );
}
